import { useEffect, useRef, useState } from 'react';
import { readReleaseMarker, saveReleaseMarker } from '@/lib/releaseReadState';

/** The caller remounts on identity/release changes, isolating drafts and fallback state. */
export function useReleaseReadState(key: string | null, releaseId: string | undefined) {
  const fallbackViewed = useRef(false);
  const [viewed, setViewed] = useState(() =>
    key && releaseId ? readReleaseMarker(key, releaseId) === true : false,
  );

  useEffect(() => {
    if (!key || !releaseId) return;
    const refresh = () => {
      const current = readReleaseMarker(key, releaseId);
      if (current !== null) setViewed(current || fallbackViewed.current);
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === key || event.key === null) refresh();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', refresh);
    // Reconcile a cross-tab write between initial render and listener registration.
    refresh();
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', refresh);
    };
  }, [key, releaseId]);

  const markViewed = () => {
    if (!key || !releaseId) return;
    setViewed(true);
    fallbackViewed.current = !saveReleaseMarker(key, releaseId);
  };
  return { unread: !!key && !!releaseId && !viewed, markViewed };
}
